import type { SxProps } from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid';

import useSWR from 'swr';
import { toast } from 'sonner';
import { useState } from 'react';

import Box from '@mui/material/Box';
import Card from '@mui/material/Card';

import axiosInstance, { fetcher } from 'src/lib/axios';
import { DashboardContent } from 'src/layouts/dashboard/content';
import { TxDataGrid } from 'src/auditoria/components/tx-data-grid';
import {
  DsNum,
  DsButton,
  GradeCanvas,
  DsPageHeader,
  DsStatusChip,
  SX_GRADE_CANVAS,
} from 'src/auditoria/components/ds';

import { formatDateBr, formatCurrency } from '../Nfse/types';

// ----------------------------------------------------------------------
// Ponte de tipo: `TxDataGridProps.sx` é `SxProps` do @mui/system (tema `{}`),
// mais estreito que o `SxProps<Theme>` que o kit publica. Os valores são os
// mesmos — só o parâmetro de tema difere.
// ----------------------------------------------------------------------

const { sx: SX_GRADE, ...PROPS_GRADE } = SX_GRADE_CANVAS;
const SX_GRADE_COMPAT = SX_GRADE as SxProps;

// ----------------------------------------------------------------------
// Contribuinte Externo → Aceitar Notas Fiscais do Município. Fila de notas
// emitidas CONTRA o contribuinte ainda não aceitas. Backend:
//  - GET  /portal-api/externo/aceitar-notas       (nf.cpfcnpj=login, aceita IS NOT TRUE)
//  - POST /portal-api/externo/aceitar-notas/:id    (grava nf.aceita = true)
//
// Composição do artboard `portal-externo-aceitar-notas.dc.html`: cabeçalho com o
// aceite em lote em `.btnsec` (contorno neutro, SEM ícone) → card de explicação
// 13px/800 + 12px secundário → casulo de grade de 6 colunas, com o `.btnpri`
// compacto de aceitar no fim de cada linha. Sem `.pager`.
// ----------------------------------------------------------------------

interface NotaPendente {
  id: number;
  numero: string | null;
  data_emissao: string;
  valor: number;
  situacao: number;
  aceita: boolean | null;
  emissor: { nome: string | null; cnpj: string | null } | null;
}
interface AceitarResponse {
  notas: NotaPendente[];
}

export default function ExternoAceitarNotasPage() {
  const { data, isLoading, mutate } = useSWR<AceitarResponse>(
    '/portal-api/externo/aceitar-notas',
    fetcher
  );
  const [aceitandoId, setAceitandoId] = useState<number | null>(null);

  const [aceitandoLote, setAceitandoLote] = useState(false);

  const aceitar = async (id: number) => {
    setAceitandoId(id);
    try {
      await axiosInstance.post(`/portal-api/externo/aceitar-notas/${id}`);
      toast.success('Nota aceita com sucesso.');
      await mutate();
    } catch {
      toast.error('Falha ao aceitar a nota.');
    } finally {
      setAceitandoId(null);
    }
  };

  // Aceite em lote — o backend só expõe o aceite por nota
  // (POST /portal-api/externo/aceitar-notas/:id), então o lote percorre a fila.
  const aceitarTodas = async (ids: number[]) => {
    setAceitandoLote(true);
    let falhas = 0;
    for (const id of ids) {
      try {
        await axiosInstance.post(`/portal-api/externo/aceitar-notas/${id}`);
      } catch {
        falhas += 1;
      }
    }
    if (falhas === 0) {
      toast.success(`${ids.length} nota(s) aceita(s).`);
    } else {
      toast.error(`${falhas} de ${ids.length} nota(s) não puderam ser aceitas.`);
    }
    await mutate();
    setAceitandoLote(false);
  };

  const columns: GridColDef<NotaPendente>[] = [
    {
      field: 'numero',
      headerName: 'Número',
      width: 110,
      valueGetter: (_v, r) => r.numero ?? '—',
      renderCell: (params) => <DsNum mono>{params.row.numero ?? '—'}</DsNum>,
    },
    {
      field: 'emissor_nome',
      headerName: 'Prestador',
      flex: 1,
      minWidth: 200,
      valueGetter: (_v, r) => r.emissor?.nome ?? r.emissor?.cnpj ?? '—',
    },
    {
      field: 'data_emissao',
      headerName: 'Emissão',
      width: 120,
      valueGetter: (_v, r) => formatDateBr(r.data_emissao),
      renderCell: (params) => <DsNum mono>{formatDateBr(params.row.data_emissao)}</DsNum>,
    },
    {
      field: 'valor',
      headerName: 'Valor (R$)',
      width: 130,
      align: 'right',
      headerAlign: 'right',
      valueGetter: (_v, r) => Number(r.valor ?? 0),
      renderCell: (params) => (
        <DsNum mono align="right" sx={{ fontWeight: 700 }}>
          {formatCurrency(params.row.valor)}
        </DsNum>
      ),
    },
    {
      field: 'situacao',
      headerName: 'Situação',
      width: 160,
      sortable: false,
      renderCell: () => <DsStatusChip tone="warning">Aguardando aceite</DsStatusChip>,
    },
    {
      field: 'acao',
      headerName: '',
      width: 150,
      align: 'right',
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <DsButton
          tone="primary"
          disabled={aceitandoLote || aceitandoId === params.row.id}
          onClick={() => aceitar(params.row.id)}
          sx={{ padding: '6px 12px', fontSize: 12 }}
        >
          Aceitar
        </DsButton>
      ),
    },
  ];

  const notas = data?.notas ?? [];
  const totalPendente = notas.reduce((soma, n) => soma + Number(n.valor ?? 0), 0);

  return (
    <DashboardContent>
      <DsPageHeader
        title="Aceitar Notas do Município"
        weight={800}
        subtitle="Notas emitidas contra seu CNPJ aguardando confirmação."
        action={
          <DsButton
            tone="neutral"
            size="tall"
            disabled={aceitandoLote || notas.length === 0}
            onClick={() => aceitarTodas(notas.map((n) => n.id))}
          >
            Aceitar todas ({notas.length})
          </DsButton>
        }
      />

      <Card sx={{ padding: '16px 18px', mb: '12px' }}>
        <Box component="p" sx={{ fontSize: 13, fontWeight: 800, m: 0 }}>
          Notas emitidas contra seu CNPJ aguardando aceite
        </Box>
        <Box component="p" sx={{ fontSize: 12, color: 'text.secondary', m: '2px 0 0' }}>
          O aceite confirma o tomador e libera a escrituração · {notas.length} nota(s) ·{' '}
          <DsNum>{formatCurrency(totalPendente)}</DsNum>
        </Box>
      </Card>

      <GradeCanvas>
        <TxDataGrid<NotaPendente>
          rows={notas}
          columns={columns}
          loading={isLoading}
          getRowId={(row) => row.id}
          emptyLabel="Nenhuma nota aguardando aceite"
          autoHeight
          hideFooter
          {...PROPS_GRADE}
          sx={SX_GRADE_COMPAT}
        />
      </GradeCanvas>
    </DashboardContent>
  );
}
